import React, { useMemo, useState } from "react";
import { CalendarDays, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { getMedicationColor } from "@/lib/medicationColors";

/**
 * MedicationDateStrip
 * --------------------
 * Sits between the patient-header card and the calendar grid on the Treatment
 * Calendar page. Lists ONLY the dates that carry at least one Drug
 * Administration event and shows a Switch button per date for "overall
 * completion of that day". Toggling it calls POST /calendars/{id}/day-completion
 * which flips every drug event on that date to status="completed" /
 * med_status="administered". The ChemoAnalytics → Wellness page Medication
 * Adherence card then re-derives its percentages from that data.
 */
export default function MedicationDateStrip({ calId, events = [], onChanged, onSelectEvent }) {
  const [busyDate, setBusyDate] = useState(null);
  const [page, setPage] = useState(0);

  const dateGroups = useMemo(() => {
    const map = new Map();
    (events || [])
      // Include ALL event types so Lab Draw / Clinic Visit / Custom etc. also
      // surface on the desktop Medication Date Card alongside drug chips.
      .filter((e) => e.status !== "skipped")
      .forEach((e) => {
        const k = e.date;
        if (!map.has(k)) map.set(k, { date: k, drugs: [], other: [], events: [], confirmation: null });
        const g = map.get(k);
        g.events.push(e);
        if (e.confirmation && !g.confirmation) g.confirmation = e.confirmation;
        if (e.event_type === "Drug Administration") {
          const drugName = e.print_name || e.drug_name || e.name;
          if (!g.drugs.find((d) => d.name === drugName)) {
            g.drugs.push({ name: drugName, color: getMedicationColor(drugName), event: e });
          }
        } else {
          g.other.push({ type: e.event_type, event: e });
        }
      });
    return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
  }, [events]);

  // Each day is "completed" when every non-skipped drug event on it is completed.
  const dayCompletionMap = useMemo(() => {
    const m = {};
    dateGroups.forEach((g) => {
      const live = g.events.filter((e) => e.event_type === "Drug Administration" && e.status !== "skipped");
      m[g.date] = live.length > 0 && live.every(
        (e) => e.status === "completed" || e.med_status === "administered"
      );
    });
    return m;
  }, [dateGroups]);

  const PAGE_SIZE = 6;
  const totalPages = Math.max(1, Math.ceil(dateGroups.length / PAGE_SIZE));
  const visible = dateGroups.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  if (dateGroups.length === 0) return null;

  const formatHeading = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return {
      weekday: d.toLocaleDateString(undefined, { weekday: "short" }),
      month: d.toLocaleDateString(undefined, { month: "short" }),
      day: d.getDate(),
    };
  };

  const toggle = async (date, currentlyCompleted) => {
    if (busyDate) return;
    setBusyDate(date);
    try {
      await api.post(`/calendars/${calId}/day-completion`, {
        date,
        completed: !currentlyCompleted,
      });
      onChanged && onChanged();
    } catch (err) {
      window.alert("Could not update completion. Please retry.");
    } finally {
      setBusyDate(null);
    }
  };

  return (
    <div
      className="bg-white border border-stone-200 rounded-xl shadow-soft p-4"
      data-testid="medication-date-strip"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-brand-500" />
          <h3 className="font-display font-semibold text-stone-900">Medication Dates</h3>
          <span className="text-xs text-stone-500">
            Flip the switch when the day's medication has been administered. The Wellness Medication Adherence card updates automatically.
          </span>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-1.5 rounded-lg border border-stone-300 hover:bg-stone-50 disabled:opacity-40"
              data-testid="med-strip-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-stone-500 px-1">{page + 1} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="p-1.5 rounded-lg border border-stone-300 hover:bg-stone-50 disabled:opacity-40"
              data-testid="med-strip-next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {visible.map((g) => {
          const h = formatHeading(g.date);
          const completed = dayCompletionMap[g.date];
          return (
            <div
              key={g.date}
              className={`medication-date-card rounded-xl p-3 ${completed ? "is-completed" : ""}`}
              data-testid={`med-date-card-${g.date}`}
            >
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider med-date-day">{h.weekday}</div>
                  <div className="font-display font-bold text-stone-900 leading-tight">
                    {h.month} {h.day}
                  </div>
                </div>
                {completed && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
              </div>

              {g.confirmation && (
                <div className="mt-2">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                    g.confirmation === "confirmed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                    g.confirmation === "tentative" ? "bg-amber-50 text-amber-700 border-amber-200" :
                    "bg-rose-50 text-rose-700 border-rose-200"
                  }`} data-testid={`med-date-confirmation-${g.date}`}>
                    Patient: {g.confirmation[0].toUpperCase() + g.confirmation.slice(1)}
                  </span>
                </div>
              )}

              <div className="mt-2 flex flex-wrap gap-1">
                {g.drugs.map((d) => (
                  <button
                    key={d.name}
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSelectEvent && onSelectEvent(d.event); }}
                    className="px-1.5 py-0.5 rounded text-[10px] font-semibold truncate max-w-[100px] hover:brightness-110 hover:ring-2 hover:ring-offset-1 hover:ring-stone-300 transition focus:outline-none focus:ring-2 focus:ring-brand-500"
                    style={{ backgroundColor: d.color.bg, color: d.color.text }}
                    title={`${d.name} — open Drug Administration details`}
                    data-testid={`med-date-chip-${g.date}-${d.name.replace(/\W+/g, "-")}`}
                  >
                    {d.name}
                  </button>
                ))}
                {g.other.map((o, idx) => (
                  <button
                    key={`oth-${idx}`}
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSelectEvent && onSelectEvent(o.event); }}
                    className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-stone-100 text-stone-700 border border-stone-200 hover:bg-stone-200 transition"
                    title={`${o.type} — open event`}
                    data-testid={`med-date-other-${g.date}-${o.type.replace(/\W+/g, "-")}`}
                  >
                    {o.type}
                  </button>
                ))}
              </div>

              <label
                className="mt-3 flex items-center justify-between text-xs gap-2 cursor-pointer select-none"
                data-testid={`med-date-switch-label-${g.date}`}
              >
                <span className={`font-medium ${completed ? "text-emerald-700" : "text-stone-600"}`}>
                  {completed ? "Completed" : "Mark completed"}
                </span>
                <SwitchToggle
                  checked={!!completed}
                  busy={busyDate === g.date}
                  onChange={() => toggle(g.date, !!completed)}
                  testid={`med-date-switch-${g.date}`}
                />
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SwitchToggle({ checked, busy, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={(e) => { e.preventDefault(); onChange(); }}
      disabled={busy}
      data-testid={testid}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-60 ${
        checked ? "bg-emerald-500" : "bg-stone-300"
      }`}
    >
      <span
        aria-hidden="true"
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );
}
